// code-guardian-ignore-duplicates: parallel Sales & Targets tab layout; the load try/catch/finally, xlsx download and loading/error returns mirror ResultsTab, each with its own endpoint and messages
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, CheckCircle2 } from 'lucide-react';
import { salesTargetsAPI } from '../../services/api';
import { downloadXlsx } from '../../utils/download';
import { SearchInput } from '../../components/SearchInput';
import PaginationFooter from '../../components/PaginationFooter';
import { usePagination } from '../../hooks/usePagination';
import { PageErrorBanner } from '../../components/PageErrorBanner';
import { BlockLoader } from '../../components/loading';
import {
  coordKey, formatNumber, ordinalDay,
  type CoordinateOption, type MonthComputation, type SalesTargetConfig,
} from './types';

/**
 * What has no target yet.
 *
 * The user's own requirement, and the one a monthly upload most needs: "the
 * system should highlight which stores' targets have not been set yet". It is
 * its own tab rather than a banner because it is a WORKLIST — you come here,
 * download it, fill it in, and come back.
 *
 * Two states are deliberately kept apart. "Not set" means nobody decided and
 * nothing is standing in. "Using the default" means a figure exists but it is
 * the fallback, not a decision. Collapsing them into one "missing" count would
 * hide the first behind the second, and only the first is urgent.
 */

interface Props {
  projectId: number;
  month: string;
  config: SalesTargetConfig;
}

const PAGE_SIZES = [
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

/**
 * A tile that is also the filter for what it counts.
 *
 * "22 on a default" is a number an admin wants to click — asking them to find a
 * checkbox instead is a step for no reason. `onClick` makes the tile the control;
 * `active` shows which one the table below is obeying.
 */
function Tile({ label, value, tone = 'plain', hint, onClick, active }: {
  label: string; value: string; tone?: 'plain' | 'warn' | 'good'; hint?: string;
  onClick?: () => void; active?: boolean;
}) {
  const toneClass = tone === 'warn'
    ? 'border-amber-200 bg-amber-50'
    : tone === 'good' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white';
  const valueClass = tone === 'warn'
    ? 'text-amber-900' : tone === 'good' ? 'text-green-900' : 'text-gray-900';
  const body = (
    <>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${valueClass}`}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </>
  );
  if (!onClick) return <div className={`rounded-xl border px-4 py-3 ${toneClass}`}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border px-4 py-3 text-left transition-shadow hover:shadow-sm ${toneClass}`
        + `${active ? ' ring-2 ring-primary-400' : ''}`}
    >
      {body}
    </button>
  );
}

export default function CoverageTab({ projectId, month, config }: Props) {
  const [data, setData] = useState<MonthComputation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** 'none' = nothing set, 'default' = a fallback is standing in, 'all' = both. */
  const [gapFilter, setGapFilter] = useState<'all' | 'none' | 'default'>('none');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  /**
   * undefined = not chosen yet, '' = every product, otherwise one coordinate key.
   *
   * The third state matters: the coverage call is the expensive one, and firing
   * it once for the whole project and again for the default product would make
   * every visit to this tab compute 15,000 rows nobody asked to see.
   */
  const [selectedCoord, setSelectedCoord] = useState<string | undefined>(undefined);

  // Land on the FIRST product rather than the whole project. "15,684 not set" is
  // a number nobody can act on; "2,614 promoters have no iPhone target" is.
  useEffect(() => {
    let cancelled = false;
    salesTargetsAPI.listCoordinates(projectId, config.productGranularity)
      .then((res) => {
        if (cancelled) return;
        const coords: CoordinateOption[] = res.data.data ?? [];
        setSelectedCoord(coords.length > 0 ? coordKey(coords[0].coord) : '');
      })
      .catch(() => { if (!cancelled) setSelectedCoord(''); });
    return () => { cancelled = true; };
  }, [projectId, config.productGranularity]);

  // The product goes to the SERVER, not into a client-side filter. `gaps` is
  // capped server-side, so filtering the returned array would show nothing at all
  // for any product past the first once a project has more rows than the cap.
  const load = useCallback(async () => {
    if (selectedCoord === undefined) return;
    setLoading(true);
    setError(null);
    try {
      const res = await salesTargetsAPI.getComputed(projectId, {
        month, withSales: false, coordKeys: selectedCoord || undefined,
      });
      setData(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || `Could not work out coverage for ${month}.`);
    } finally {
      setLoading(false);
    }
  }, [projectId, month, selectedCoord]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, gapFilter, selectedCoord]);

  const gaps = useMemo(() => {
    const all = data?.coverage.gaps ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((g) => {
      if (gapFilter !== 'all' && g.state !== gapFilter) return false;
      if (!q) return true;
      return g.code.toLowerCase().includes(q)
        || g.name.toLowerCase().includes(q)
        || g.productLabel.toLowerCase().includes(q);
    });
  }, [data, search, gapFilter]);

  const { totalPages, safePage, paginatedData } = usePagination(gaps, page, pageSize);

  const download = async () => {
    try {
      const res = await salesTargetsAPI.downloadCoverage(projectId, month, selectedCoord || undefined);
      downloadXlsx(`sales_target_coverage_${month}.xlsx`, res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not build the download.');
    }
  };

  if (loading) {
    return (
      <BlockLoader className="rounded-xl border border-gray-200 bg-white py-20" />
    );
  }
  if (!data) return error ? <PageErrorBanner message={error} /> : null;

  const { coverage } = data;
  const subjectNoun = config.sourceScope === 'store' ? 'store' : 'promoter';
  const nothingMissing = coverage.linesMissing === 0;
  const selectedLabel = selectedCoord
    ? coverage.byProduct.find((p) => `${p.productId}::${p.skuId ?? ''}` === selectedCoord)?.productLabel ?? null
    : null;
  const projectWideMissing = coverage.byProduct.reduce((n, p) => n + p.missing, 0);

  return (
    <div className="space-y-4">
      {error && <PageErrorBanner message={error} />}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label={`${subjectNoun}s`}
          value={formatNumber(coverage.subjectsTotal)}
          hint={selectedLabel
            ? `${formatNumber(coverage.subjectsWithAnyTarget)} have a ${selectedLabel} target`
            : `${formatNumber(coverage.subjectsWithAnyTarget)} have at least one target`}
        />
        <Tile
          label="Targets set"
          value={formatNumber(coverage.linesExplicit)}
          hint={selectedLabel
            ? `of ${formatNumber(coverage.linesExpected)} ${subjectNoun}s`
            : `of ${formatNumber(coverage.linesExpected)} ${subjectNoun}/product combinations`}
        />
        <Tile
          label="On a default"
          value={formatNumber(coverage.linesOnDefault)}
          hint={config.applyDefaults
            ? (coverage.linesOnDefault > 0 ? 'Click to see which' : 'A fallback is standing in')
            : 'Defaults are switched off'}
          onClick={coverage.linesOnDefault > 0 ? () => setGapFilter('default') : undefined}
          active={gapFilter === 'default'}
        />
        <Tile
          label="Not set"
          value={formatNumber(coverage.linesMissing)}
          tone={nothingMissing ? 'good' : 'warn'}
          hint={nothingMissing ? 'Nothing outstanding' : 'Click to see which'}
          onClick={coverage.linesMissing > 0 ? () => setGapFilter('none') : undefined}
          active={gapFilter === 'none'}
        />
      </div>

      {/* Looking at one product hides the project-wide position, so it is kept in
          view here rather than only in a dropdown nobody has opened. */}
      {selectedLabel && projectWideMissing > coverage.linesMissing && (
        <p className="text-xs text-gray-500">
          Showing {selectedLabel} only. Across all {coverage.byProduct.length} products,{' '}
          <strong className="text-gray-700">{formatNumber(projectWideMissing)}</strong>{' '}
          {subjectNoun}/product combinations have no target.
        </p>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="cov-product">
              Product
            </label>
            <select
              id="cov-product"
              value={selectedCoord ?? ''}
              onChange={(e) => setSelectedCoord(e.target.value)}
              className="min-w-[18rem] h-[38px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              {/* Each option carries its own outstanding count, so choosing which
                  product to work on does not need a second screen. */}
              {coverage.byProduct.map((p) => (
                <option key={`${p.productId}::${p.skuId ?? ''}`} value={`${p.productId}::${p.skuId ?? ''}`}>
                  {p.productLabel}
                  {p.missing > 0 ? ` — ${p.missing.toLocaleString()} not set` : ' — all set'}
                </option>
              ))}
              <option value="">
                All products — {formatNumber(projectWideMissing)} not set
              </option>
            </select>
          </div>
          <div className="w-64">
            <SearchInput
              size="md"
              value={search}
              onChange={setSearch}
              placeholder={`Search ${subjectNoun}${selectedCoord ? '' : ' or product'}`}
            />
          </div>
        </div>
        {/* The filter sits with the download rather than between the two fields:
            it changes what the list below shows, like the download acts on it,
            while Product and Search are the things being typed into. */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            {([
              ['none', 'Not set'],
              ['default', 'On a default'],
              ['all', 'Both'],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setGapFilter(key)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  gapFilter === key ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={download}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Download the list
          </button>
        </div>
      </div>

      {gaps.length === 0 ? (
        <div className="rounded-xl border border-green-200 bg-green-50 px-6 py-12 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-green-500" />
          <h3 className="mt-3 text-base font-medium text-green-900">
            {gapFilter === 'none' && coverage.linesMissing === 0
              ? selectedLabel
                ? `Every ${subjectNoun} has a ${selectedLabel} target for ${month}`
                : `Every ${subjectNoun} has a target for ${month}`
              : gapFilter === 'default' && coverage.linesOnDefault === 0
                ? 'No default is standing in for anything here'
                : 'Nothing matches that search'}
          </h3>
          {gapFilter === 'none' && coverage.linesOnDefault > 0 && coverage.linesMissing === 0 && (
            <p className="mt-2 text-sm text-green-800">
              {formatNumber(coverage.linesOnDefault)} of them are running on a default —{' '}
              <button
                type="button"
                onClick={() => setGapFilter('default')}
                className="font-medium underline underline-offset-2"
              >
                see which
              </button>
              .
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">{config.sourceScope === 'store' ? 'Store' : 'Promoter'}</th>
                <th className="px-4 py-3">Name</th>
                {!selectedCoord && <th className="px-4 py-3">Product</th>}
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedData.map((g) => (
                <tr key={`${g.key}-${g.coord.productId}-${g.coord.skuId ?? ''}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-900">{g.code}</td>
                  <td className="px-4 py-2 text-gray-600">{g.name}</td>
                  {!selectedCoord && <td className="px-4 py-2 text-gray-600">{g.productLabel}</td>}
                  <td className="px-4 py-2">
                    {/* Only the exception is coloured — a row on a default is
                        fine, a row with nothing is the thing to act on. */}
                    {g.state === 'none' ? (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        Not set
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">
                        Using the default · {formatNumber(g.defaultValue)}
                        {g.defaultFromDay ? ` from the ${ordinalDay(g.defaultFromDay)}` : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationFooter
            safePage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZES}
            totalRows={gaps.length}
            onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
            onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
            onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </div>
      )}

      {data.truncated && (
        <p className="text-xs text-gray-500">
          The list on screen is capped. Download it for the complete picture
          {coverage.gapCount > coverage.gaps.length
            && ` — ${formatNumber(coverage.gapCount)} rows in total`}.
        </p>
      )}
    </div>
  );
}
